import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi";
import {
  ApiError,
  type HealthCpuReport,
  type HealthResourceReport,
  type ThresholdStatus,
  api,
} from "../lib/api";
import {
  type HealthSortKey,
  type SortDirection,
  formatMiB,
  sortHealthApps,
} from "../lib/health-format";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i] ?? "TiB"}`;
}

function StatusBadge({ status }: { status: ThresholdStatus }): JSX.Element | null {
  const { t } = useTranslation();
  if (status === "ok") return null;
  return (
    <span className={`health-badge health-badge--${status}`}>
      {t(`admin.health.status.${status}`)}
    </span>
  );
}

function UsageCard({ title, usage }: { title: string; usage: HealthResourceReport }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={`health-card health-card--${usage.status}`}>
      <h3>
        {title} <StatusBadge status={usage.status} />
      </h3>
      <p className="health-card__value">{usage.usedPercent}%</p>
      <p className="health-card__sub">
        {t("admin.health.usedOfTotal", {
          used: formatBytes(usage.usedBytes),
          total: formatBytes(usage.totalBytes),
          percent: usage.usedPercent,
        })}
      </p>
    </div>
  );
}

function CpuCard({ cpu }: { cpu: HealthCpuReport }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={`health-card health-card--${cpu.status}`}>
      <h3>
        {t("admin.health.cpu")} <StatusBadge status={cpu.status} />
      </h3>
      <p className="health-card__value">{cpu.usedPercent}%</p>
      <p className="health-card__sub">
        {t("admin.health.load")}: {cpu.load1.toFixed(2)} / {cpu.load5.toFixed(2)} /{" "}
        {cpu.load15.toFixed(2)} · {cpu.cores} cores
      </p>
    </div>
  );
}

function SortHeader({
  label,
  column,
  active,
  direction,
  numeric,
  onSort,
}: {
  label: string;
  column: HealthSortKey;
  active: boolean;
  direction: SortDirection;
  numeric: boolean;
  onSort: (key: HealthSortKey) => void;
}): JSX.Element {
  const className = [
    "health-th--sortable",
    numeric ? "health-th--num" : "",
    active ? "health-th--active" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <th
      className={className}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button type="button" className="health-th__btn" onClick={() => onSort(column)}>
        {label}
        <span className="health-th__caret" aria-hidden="true">
          {active ? (direction === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

export function AdminHealth(): JSX.Element {
  const { t } = useTranslation();
  const result = useApi(() => api.admin.health(), []);
  const [sortKey, setSortKey] = useState<HealthSortKey>("totalBytes");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  function handleSort(key: HealthSortKey): void {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Text starts ascending (A→Z), numbers start descending (biggest first).
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  // On-demand auto-refresh while the page is open (~15 s); reload keeps the
  // last good data on screen (no loading flash).
  const reload = result.reload;
  useEffect(() => {
    const id = setInterval(reload, 15_000);
    return () => clearInterval(id);
  }, [reload]);

  if (result.status === "loading") {
    return <p className="launchpad__hint">{t("admin.loading")}</p>;
  }
  if (result.status === "error") {
    const msg =
      result.error instanceof ApiError
        ? (result.error.detail ?? result.error.message)
        : result.error.message;
    return <p className="launchpad__hint launchpad__hint--error">{msg}</p>;
  }

  const { host, apps, docker_available } = result.data;

  return (
    <section className="admin">
      <h2>{t("admin.healthTitle")}</h2>
      <div className="health-cards">
        <CpuCard cpu={host.cpu} />
        <UsageCard title={t("admin.health.memory")} usage={host.memory} />
        {host.disk ? (
          <UsageCard title={t("admin.health.disk")} usage={host.disk} />
        ) : (
          <div className="health-card">
            <h3>{t("admin.health.disk")}</h3>
            <p className="health-card__sub">{t("admin.health.diskUnavailable")}</p>
          </div>
        )}
      </div>

      <h3>{t("admin.health.appsTitle")}</h3>
      {!docker_available ? (
        <p className="launchpad__hint launchpad__hint--error">
          {t("admin.health.dockerUnavailable")}
        </p>
      ) : (
        <table className="admin__table">
          <thead>
            <tr>
              <SortHeader
                label={t("admin.health.col.app")}
                column="name"
                active={sortKey === "name"}
                direction={sortDir}
                numeric={false}
                onSort={handleSort}
              />
              <SortHeader
                label={t("admin.health.col.cpu")}
                column="cpuPercent"
                active={sortKey === "cpuPercent"}
                direction={sortDir}
                numeric
                onSort={handleSort}
              />
              <SortHeader
                label={t("admin.health.col.memory")}
                column="memUsageBytes"
                active={sortKey === "memUsageBytes"}
                direction={sortDir}
                numeric
                onSort={handleSort}
              />
              <SortHeader
                label={t("admin.health.col.volumes")}
                column="volumesBytes"
                active={sortKey === "volumesBytes"}
                direction={sortDir}
                numeric
                onSort={handleSort}
              />
              <SortHeader
                label={t("admin.health.col.writable")}
                column="writableBytes"
                active={sortKey === "writableBytes"}
                direction={sortDir}
                numeric
                onSort={handleSort}
              />
              <SortHeader
                label={t("admin.health.col.total")}
                column="totalBytes"
                active={sortKey === "totalBytes"}
                direction={sortDir}
                numeric
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {sortHealthApps(apps, sortKey, sortDir).map((a) => (
              <tr key={a.slug}>
                <td>
                  {a.name} <code>{a.slug}</code>
                </td>
                <td className="health-num">{a.cpuPercent === null ? "—" : `${a.cpuPercent}%`}</td>
                <td className="health-num">{formatMiB(a.memUsageBytes)}</td>
                <td className="health-num">{formatMiB(a.volumesBytes)}</td>
                <td className="health-num">{formatMiB(a.writableBytes)}</td>
                <td className="health-num">{formatMiB(a.totalBytes)}</td>
              </tr>
            ))}
            {apps.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin__empty">
                  {t("admin.health.empty")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </section>
  );
}
